import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trophy, Clock, ArrowLeft, Check, ArrowRight, Zap, Loader2, Download, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { generateReceipt } from '../utils/receipt';
import { TurfDatePicker } from '../components/TurfDatePicker';
import { SEO } from '../components/SEO';
import axios from 'axios';
import { format, addDays } from 'date-fns';

import { API_URL } from '../utils/api';

const ALL_SLOTS = [
  '03:00','04:00','05:00','06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00',
  '15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00','23:00','00:00','01:00','02:00'
];

const HAPPY_SLOTS = new Set(['03:00','04:00','05:00','06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00']);

const isWeekend = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr + 'T00:00:00');
  return d.getDay() === 0 || d.getDay() === 6;
};

const getSlotRate = (slot, dateStr) => {
  const weekend = isWeekend(dateStr);
  if (HAPPY_SLOTS.has(slot)) return weekend ? 400 : 300;
  return weekend ? 500 : 400;
};

const getSlotType = (slot) => HAPPY_SLOTS.has(slot) ? 'happy' : 'rush';

const formatSlotLabel = (slot) => {
  const hour = parseInt(slot.split(':')[0]);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${display}:00 ${suffix}`;
};

const DEFAULT_TURF_ID = 'turf_cricket01';

function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  useEffect(() => {
    const orderId = searchParams.get('order_id');
    const bookingId = searchParams.get('booking_id');
    if (orderId && bookingId) {
      verifyPayment(orderId, bookingId);
    }
  }, [searchParams]);

  useEffect(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    setSelectedDate(today);
  }, []);

  useEffect(() => {
    if (selectedDate) fetchAvailability();
  }, [selectedDate]);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/turfs/${DEFAULT_TURF_ID}/availability?date=${selectedDate}`);
      setBookedSlots(response.data.booked_slots);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const verifyPayment = async (orderId, bookingId) => {
    setVerifying(true);
    setStep(3);
    try {
      const response = await axios.get(`${API_URL}/api/payments/verify?order_id=${orderId}&booking_id=${bookingId}`);
      setPaymentResult(response.data);
      try {
        const bookingRes = await axios.get(`${API_URL}/api/bookings/${bookingId}`, { withCredentials: true });
        setConfirmedBooking(bookingRes.data);
      } catch (e) { console.error('Could not fetch booking details:', e); }
    } catch (error) {
      setPaymentResult({ status: 'failed', message: 'Could not verify payment' });
    } finally {
      setVerifying(false);
    }
  };

  const toggleSlot = (slot) => {
    if (bookedSlots.includes(slot)) return;
    if (selectedSlots.includes(slot)) {
      setSelectedSlots(selectedSlots.filter(s => s !== slot));
    } else {
      setSelectedSlots([...selectedSlots, slot]);
    }
  };

  const calculateTotal = () => {
    return selectedSlots.reduce((sum, slot) => sum + getSlotRate(slot, selectedDate), 0);
  };

  const handleBack = () => {
    if (step === 1) navigate('/dashboard');
    else if (step === 2) setStep(1);
  };

  const handleProceedToConfirm = () => {
    if (selectedSlots.length === 0) return;
    setStep(2);
  };

  const handlePayNow = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) return;
    setLoading(true);
    try {
      const bookingRes = await axios.post(`${API_URL}/api/bookings`, {
        turf_id: DEFAULT_TURF_ID,
        booking_date: selectedDate,
        time_slots: selectedSlots,
        booking_type: 'mixed',
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: `+91${customerInfo.phone}`
      }, { withCredentials: true });
      const booking = bookingRes.data;

      // Step 1: Create order on backend
      const orderRes = await axios.post(`${API_URL}/api/payments/create-order`, { booking_id: booking.booking_id }, { withCredentials: true });
      const { payment_session_id, order_id: cfOrderId } = orderRes.data;

      if (!payment_session_id) {
        alert('Payment session not created. Please try again.');
        return;
      }

      // Step 2: Open Cashfree Popup Checkout
      const cashfree = window.Cashfree({ mode: 'production' });
      
      cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: '_modal'
      }).then(function(result) {
        if (result.error) {
          console.error('Payment error:', result.error);
          alert(result.error.message || 'Payment failed. Please try again.');
        }
        if (result.redirect) {
          console.log('Payment redirect');
        }
        if (result.paymentDetails) {
          console.log('Payment completed:', result.paymentDetails);
          // Verify payment on backend
          window.location.href = `${window.location.origin}/booking?order_id=${cfOrderId}&booking_id=${booking.booking_id}`;
        }
      });
    } catch (error) {
      console.error('Payment initiation failed:', error);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const weekend = isWeekend(selectedDate);
  const stepLabels = ['Select Slots', 'Confirm & Pay'];

  // Payment verification screen
  if (step === 3) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Nav navigate={navigate} />
        <div className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-md mx-auto text-center py-12">
            {verifying ? (
              <div className="bg-[#171717] border border-[#1A1A1A] p-12" style={{ borderRadius: '12px' }}>
                <Loader2 className="h-12 w-12 text-[#16A34A] animate-spin mx-auto mb-6" />
                <h2 className="text-2xl font-bold mb-2">Verifying Payment...</h2>
                <p className="text-gray-500 font-body text-sm">Please wait while we confirm your payment</p>
              </div>
            ) : paymentResult?.status === 'confirmed' ? (
              <div className="bg-[#171717] border border-[#1A1A1A] overflow-hidden" style={{ borderRadius: '12px' }}>
                <div className="h-1 bg-[#16A34A]"></div>
                <div className="p-10">
                  <div className="w-20 h-20 bg-[#16A34A] flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '50%' }}>
                    <Check className="h-10 w-10 text-[#0A0A0A]" />
                  </div>
                  <h2 className="text-3xl font-bold mb-2">Booking Confirmed!</h2>
                  <p className="text-gray-500 font-body text-sm mb-8">Your payment was successful and your slot is reserved.</p>
                  {confirmedBooking && (() => {
                    const slots = confirmedBooking.time_slots || [];
                    const happySlots = slots.filter(s => HAPPY_SLOTS.has(s));
                    const rushSlots = slots.filter(s => !HAPPY_SLOTS.has(s));
                    const bDate = confirmedBooking.booking_date || '';
                    const wknd = bDate ? [0, 6].includes(new Date(bDate + 'T00:00:00').getDay()) : false;
                    const hRate = wknd ? 400 : 300;
                    const rRate = wknd ? 500 : 400;
                    return (
                    <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-5 mb-8 text-left" style={{ borderRadius: '8px' }}>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500 font-body">Date</span>
                          <span>{confirmedBooking.booking_date}</span>
                        </div>
                        <div className="h-[1px] bg-[#1A1A1A]"></div>
                        {happySlots.length > 0 && (
                          <div>
                            <div className="text-[10px] text-gray-500 uppercase tracking-wider font-body mb-2">Happy Hours × {happySlots.length} — ₹{hRate}/hr</div>
                            <div className="flex flex-wrap gap-1.5">
                              {happySlots.map(s => (
                                <span key={s} className="px-2.5 py-1 bg-[#16A34A]/10 border border-[#16A34A]/20 text-[#16A34A] text-[11px] font-medium rounded-md">{formatSlotLabel(s)}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {rushSlots.length > 0 && (
                          <div>
                            <div className="text-[10px] text-gray-500 uppercase tracking-wider font-body mb-2">Rush Hours × {rushSlots.length} — ₹{rRate}/hr</div>
                            <div className="flex flex-wrap gap-1.5">
                              {rushSlots.map(s => (
                                <span key={s} className="px-2.5 py-1 bg-[#16A34A]/10 border border-[#16A34A]/20 text-[#16A34A] text-[11px] font-medium rounded-md">{formatSlotLabel(s)}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="h-[1px] bg-[#1A1A1A]"></div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 font-body">Total Paid</span>
                          <span className="text-xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>₹{confirmedBooking.total_amount}</span>
                        </div>
                      </div>
                    </div>
                    );
                  })()}
                  <div className="flex flex-col gap-3">
                    <button onClick={() => confirmedBooking && generateReceipt(confirmedBooking)} data-testid="download-receipt-button" className="btn-primary w-full inline-flex items-center justify-center gap-2">
                      <Download className="h-4 w-4" /> Download Receipt
                    </button>
                    <button onClick={() => navigate('/dashboard')} data-testid="view-bookings-button" className="btn-secondary w-full inline-flex items-center justify-center gap-2">
                      View Bookings
                    </button>
                  </div>
                </div>
              </div>
            ) : paymentResult?.status === 'pending' ? (
              <div className="bg-[#171717] border border-[#1A1A1A] p-10" style={{ borderRadius: '12px' }}>
                <div className="w-16 h-16 bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '50%' }}>
                  <Clock className="h-8 w-8 text-yellow-500" />
                </div>
                <h2 className="text-3xl font-bold mb-2">Payment Pending</h2>
                <p className="text-gray-500 font-body text-sm mb-8">Your payment is being processed.</p>
                <button onClick={() => navigate('/dashboard')} className="btn-primary w-full">Go to Dashboard</button>
              </div>
            ) : (
              <div className="bg-[#171717] border border-[#1A1A1A] p-10" style={{ borderRadius: '12px' }}>
                <div className="w-16 h-16 bg-red-500/20 border border-red-500/30 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '50%' }}>
                  <span className="text-red-400 text-2xl font-bold">!</span>
                </div>
                <h2 className="text-3xl font-bold mb-2">Payment Failed</h2>
                <p className="text-gray-500 font-body text-sm mb-8">{paymentResult?.message || 'Something went wrong.'}</p>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => { setStep(1); setSelectedSlots([]); setPaymentResult(null); setConfirmedBooking(null); navigate('/booking'); }} className="btn-primary">Try Again</button>
                  <button onClick={() => navigate('/dashboard')} className="btn-secondary">Dashboard</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="Book a Slot"
        description="Reserve your cricket or football turf slot at Malda Turf. Pick a date, choose hours, and pay online."
        path="/booking"
        noindex
      />
      <Nav navigate={navigate} />
      <div className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Back + Header */}
          <div className="mb-8">
            <button onClick={handleBack} data-testid="back-button" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-white transition-colors mb-6 font-body">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Book Your Slot</h1>
            <p className="text-gray-500 font-body text-sm">
              {step === 1 && 'Pick a date and select your time slots'}
              {step === 2 && 'Fill your details and pay'}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mb-10">
            {stepLabels.map((label, i) => {
              const num = i + 1;
              const active = step >= num;
              const completed = step > num;
              return (
                <React.Fragment key={num}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 flex items-center justify-center text-xs font-semibold ${active ? 'bg-[#16A34A] text-[#0A0A0A]' : 'bg-[#1A1A1A] text-gray-500'}`} style={{ borderRadius: '4px' }}>
                      {completed ? <Check className="h-4 w-4" /> : num}
                    </div>
                    <span className={`text-xs font-body hidden sm:inline ${active ? 'text-white' : 'text-gray-600'}`}>{label}</span>
                  </div>
                  {num < 2 && <div className={`flex-1 h-[2px] mx-2 ${step > num ? 'bg-[#16A34A]' : 'bg-[#1A1A1A]'}`} />}
                </React.Fragment>
              );
            })}
          </div>

          {/* ─── Step 1: Date & All Slots ─── */}
          {step === 1 && (
            <div className="space-y-8">
              {/* Pricing Info */}
              <div className="bg-[#171717] border border-[#1A1A1A] rounded-xl overflow-hidden">
                <div className="grid grid-cols-2 divide-x divide-[#1A1A1A]">
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-3.5 w-3.5 text-[#16A34A]" />
                      <span className="text-[13px] font-semibold font-heading">Happy Hours</span>
                      <span className="text-[10px] text-gray-500 font-body">3AM–3PM</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div><span className="text-[10px] text-gray-600 font-body block">Mon-Fri</span><span className="text-lg font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>₹300<span className="text-xs text-gray-600 font-normal">/hr</span></span></div>
                      <div><span className="text-[10px] text-gray-600 font-body block">Sat-Sun</span><span className="text-lg font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>₹400<span className="text-xs text-gray-600 font-normal">/hr</span></span></div>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="h-3.5 w-3.5 text-[#16A34A]" />
                      <span className="text-[13px] font-semibold font-heading">Rush Hours</span>
                      <span className="text-[10px] text-gray-500 font-body">3PM–3AM</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div><span className="text-[10px] text-gray-600 font-body block">Mon-Fri</span><span className="text-lg font-bold text-[#16A34A]" style={{ fontFamily: 'Inter, sans-serif' }}>₹400<span className="text-xs text-gray-600 font-normal">/hr</span></span></div>
                      <div><span className="text-[10px] text-gray-600 font-body block">Sat-Sun</span><span className="text-lg font-bold text-[#16A34A]" style={{ fontFamily: 'Inter, sans-serif' }}>₹500<span className="text-xs text-gray-600 font-normal">/hr</span></span></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Date Picker */}
              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wider mb-4 font-body">Select Date</label>
                <div className="max-w-xs">
                  <TurfDatePicker
                    value={selectedDate}
                    onChange={(d) => { setSelectedDate(d); setSelectedSlots([]); }}
                    minDate={new Date()}
                    maxDate={addDays(new Date(), 30)}
                    showWeekendBadge
                    testId="date-picker-trigger"
                  />
                </div>
              </div>

              {/* All Time Slots */}
              <div>
                <label className="block text-xs text-gray-400 uppercase tracking-wider mb-4 font-body">
                  Available Slots &middot; {weekend ? 'Weekend' : 'Weekday'} Rates
                </label>

                {/* Happy Hours */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Clock className="h-3 w-3 text-gray-500" />
                    <span className="text-[11px] text-gray-500 font-body">Happy Hours (3AM–3PM) · ₹{weekend ? '400' : '300'}/hr</span>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2">
                    {ALL_SLOTS.filter(s => HAPPY_SLOTS.has(s)).map((slot) => {
                      const isBooked = bookedSlots.includes(slot);
                      const isSelected = selectedSlots.includes(slot);
                      return (
                        <button key={slot} onClick={() => toggleSlot(slot)} disabled={isBooked} data-testid={`time-slot-${slot}`}
                          className={`py-2.5 border text-center transition-colors ${isBooked ? 'border-[#1A1A1A] bg-[#0A0A0A] text-gray-700 cursor-not-allowed line-through' : isSelected ? 'border-[#16A34A] bg-[#16A34A] text-[#0A0A0A] font-semibold' : 'border-[#1A1A1A] bg-[#171717] hover:border-[#262626] text-gray-300'}`}
                          style={{ borderRadius: '6px' }}>
                          <span className="text-[11px]">{formatSlotLabel(slot)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Rush Hours */}
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <Zap className="h-3 w-3 text-gray-500" />
                    <span className="text-[11px] text-gray-500 font-body">Rush Hours (3PM–3AM) · ₹{weekend ? '500' : '400'}/hr</span>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2">
                    {ALL_SLOTS.filter(s => !HAPPY_SLOTS.has(s)).map((slot) => {
                      const isBooked = bookedSlots.includes(slot);
                      const isSelected = selectedSlots.includes(slot);
                      return (
                        <button key={slot} onClick={() => toggleSlot(slot)} disabled={isBooked} data-testid={`time-slot-${slot}`}
                          className={`py-2.5 border text-center transition-colors ${isBooked ? 'border-[#1A1A1A] bg-[#0A0A0A] text-gray-700 cursor-not-allowed line-through' : isSelected ? 'border-[#16A34A] bg-[#16A34A] text-[#0A0A0A] font-semibold' : 'border-[#1A1A1A] bg-[#171717] hover:border-[#262626] text-gray-300'}`}
                          style={{ borderRadius: '6px' }}>
                          <span className="text-[11px]">{formatSlotLabel(slot)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {selectedSlots.length === 0 && (
                  <p className="text-xs text-gray-600 font-body mt-3">Tap to select one or more time slots</p>
                )}
              </div>

              {/* Good to Know */}
              <div className="bg-[#171717] border border-[#1A1A1A] rounded-xl p-4 md:p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="h-3.5 w-3.5 text-[#16A34A]" />
                  <span className="text-[12px] font-semibold font-heading">Good to Know</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-3">
                    <div className="text-[9px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1 font-heading">Cricket</div>
                    <p className="text-[11px] text-gray-400 leading-relaxed font-body">Bat & ball not provided. Turf balls available for purchase.</p>
                  </div>
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-3">
                    <div className="text-[9px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1 font-heading">Football</div>
                    <p className="text-[11px] text-gray-400 leading-relaxed font-body">Football provided. Just bring your team!</p>
                  </div>
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-3">
                    <div className="text-[9px] text-[#16A34A] uppercase tracking-wider font-semibold mb-1 font-heading">Beverages</div>
                    <p className="text-[11px] text-gray-400 leading-relaxed font-body">Drinks & refreshments available at venue.</p>
                  </div>
                </div>
              </div>

              {/* Summary + Proceed */}
              {selectedSlots.length > 0 && (() => {
                const happyCount = selectedSlots.filter(s => HAPPY_SLOTS.has(s)).length;
                const rushCount = selectedSlots.filter(s => !HAPPY_SLOTS.has(s)).length;
                return (
                  <div className="bg-[#171717] border border-[#1A1A1A] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{ borderRadius: '8px' }}>
                    <div className="flex items-center gap-5">
                      <div>
                        <div className="text-[10px] text-gray-500 font-body">Slots</div>
                        <div className="text-sm font-body text-gray-300">
                          {happyCount > 0 && <span>Happy Hours × {happyCount}</span>}
                          {happyCount > 0 && rushCount > 0 && <span className="text-gray-600"> · </span>}
                          {rushCount > 0 && <span>Rush Hours × {rushCount}</span>}
                        </div>
                      </div>
                      <div className="w-[1px] h-8 bg-[#1A1A1A]"></div>
                      <div>
                        <div className="text-[10px] text-gray-500 font-body">Total</div>
                        <div className="font-bold text-lg text-[#16A34A]" style={{ fontFamily: 'Inter, sans-serif' }}>₹{calculateTotal()}</div>
                      </div>
                    </div>
                    <button onClick={handleProceedToConfirm} data-testid="proceed-button" className="btn-primary inline-flex items-center gap-2">
                      Proceed <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ─── Step 2: Customer Details + Summary + Pay ─── */}
          {step === 2 && (
            <div className="grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3">
                <div className="bg-[#171717] border border-[#1A1A1A] p-8" style={{ borderRadius: '8px' }}>
                  <h3 className="text-xl font-bold mb-6">Your Details</h3>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2 font-body">Full Name</label>
                      <input type="text" value={customerInfo.name} onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })} required data-testid="customer-name-input" className="w-full" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2 font-body">Email</label>
                      <input type="email" value={customerInfo.email} onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })} required data-testid="customer-email-input" className="w-full" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2 font-body">Phone</label>
                      <div className="flex">
                        <div className="flex items-center px-3 bg-[#1A1A1A] border border-r-0 border-[#262626] text-sm text-gray-400 font-body" style={{ borderRadius: '6px 0 0 6px' }}>+91</div>
                        <input type="tel" value={customerInfo.phone} onChange={(e) => { const val = e.target.value.replace(/\D/g, '').slice(0, 10); setCustomerInfo({ ...customerInfo, phone: val }); }} required placeholder="9876543210" maxLength={10} data-testid="customer-phone-input" className="w-full" style={{ borderRadius: '0 6px 6px 0' }} />
                      </div>
                      {customerInfo.phone && customerInfo.phone.length < 10 && (
                        <p className="text-[11px] text-yellow-500/70 mt-1 font-body">Enter 10-digit number</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-[#171717] border border-[#1A1A1A] p-6 sticky top-24" style={{ borderRadius: '8px' }}>
                  <h3 className="text-lg font-bold mb-5">Booking Summary</h3>
                  {(() => {
                    const happySlots = selectedSlots.filter(s => HAPPY_SLOTS.has(s));
                    const rushSlots = selectedSlots.filter(s => !HAPPY_SLOTS.has(s));
                    const happyRate = weekend ? 400 : 300;
                    const rushRate = weekend ? 500 : 400;
                    return (
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500 font-body">Date</span>
                          <span>{selectedDate} {weekend ? '(Weekend)' : '(Weekday)'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 font-body">Duration</span>
                          <span>{selectedSlots.length} hr{selectedSlots.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="h-[1px] bg-[#1A1A1A]"></div>
                        {happySlots.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400 font-body">Happy Hours × {happySlots.length}</span>
                            <span>₹{happyRate} × {happySlots.length} = ₹{happyRate * happySlots.length}</span>
                          </div>
                        )}
                        {rushSlots.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400 font-body">Rush Hours × {rushSlots.length}</span>
                            <span>₹{rushRate} × {rushSlots.length} = ₹{rushRate * rushSlots.length}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div className="h-[1px] bg-[#1A1A1A] my-5"></div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-sm text-gray-500 font-body">Total</span>
                    <span className="text-3xl font-bold text-[#16A34A]" style={{ fontFamily: 'Inter, sans-serif' }}>₹{calculateTotal()}</span>
                  </div>
                  <button onClick={handlePayNow} disabled={loading || !customerInfo.name || !customerInfo.email || customerInfo.phone.length !== 10} data-testid="pay-now-button" className="btn-primary w-full disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Processing...</>) : `Pay ₹${calculateTotal()}`}
                  </button>
                  <p className="text-[10px] text-gray-600 text-center mt-3 font-body">Powered by Cashfree · Secure payment</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Nav({ navigate }) {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1A1A1A]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Trophy className="h-7 w-7 text-[#16A34A]" />
            <div>
              <span className="text-xl font-bold tracking-tight">Malda Turf</span>
              <p className="text-[10px] text-gray-500 tracking-widest uppercase">Premium Sports Booking</p>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-sm text-gray-400 hover:text-white transition-colors font-body">My Dashboard</button>
        </div>
      </div>
    </nav>
  );
}

export default BookingPage;
